import NotificationsIcon from '@mui/icons-material/Notifications'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff'
import { FC, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { RootState } from 'dataStore/state'
import { getNotificationsSupportStatus } from 'handlers/getNotificationsSupportStatus'
import { openPwaInstallGuide } from 'handlers/openPwaInstallGuide'
import { requestNotificationPermission } from 'handlers/showNewOrderNotification'
import { subscribeAdminPush } from 'handlers/subscribeAdminPush'

import { NotificationsIconButton } from './EnableNotificationsButton.styles'

/**
 * Кнопка в хедере: Browser Notification + подписка Web Push.
 */
const EnableNotificationsButton: FC = () => {
  const filterPizzeriaId = useSelector(
    (state: RootState) => state.orders.filter?.pizzeria_id ?? null
  )
  const [permission, setPermission] = useState<NotificationPermission>(() => {
    if (typeof Notification === 'undefined') {
      return 'denied'
    }
    return Notification.permission
  })

  useEffect(() => {
    if (typeof Notification === 'undefined') {
      return
    }
    setPermission(Notification.permission)
  }, [])

  /**
   * Запрашивает permission ОС по клику (жест), затем подписывает на push.
   */
  const handleEnableNotifications = async (): Promise<void> => {
    const supportStatus = getNotificationsSupportStatus()

    if (supportStatus === 'needs_ios_pwa_install') {
      toast.info(
        'На iPhone/iPad уведомления работают только из установленного приложения: Поделиться → На экран «Домой», затем откройте ярлык и нажмите снова.'
      )
      openPwaInstallGuide()
      return
    }

    if (supportStatus === 'unsupported_notification_api') {
      toast.error(
        'Уведомления не поддерживаются в этом браузере. Откройте админку в Safari (iOS) или Chrome/Edge (десктоп) по HTTPS.'
      )
      return
    }

    // Первый await сразу после sync-проверок — сохраняем user gesture для диалога ОС
    const nextPermission = await requestNotificationPermission()
    setPermission(nextPermission)

    if (nextPermission === 'granted') {
      if (supportStatus === 'push_unsupported') {
        toast.warning(
          'Разрешение получено. Фоновый Web Push в этом браузере недоступен — уведомления будут при открытой вкладке.'
        )
        return
      }

      try {
        await subscribeAdminPush(
          filterPizzeriaId == null ? null : Number(filterPizzeriaId)
        )
        toast.success('Уведомления о новых заказах включены (включая фон)')
      } catch (error: any) {
        console.error(error)
        toast.warning(
          error?.message ||
            'Разрешение получено, но фоновый Web Push не удалось включить'
        )
      }
      return
    }

    if (nextPermission === 'denied') {
      toast.error(
        'Уведомления запрещены. Разрешите их в настройках браузера или в настройках установленного приложения.'
      )
      return
    }

    toast.info('Разрешение на уведомления не получено')
  }

  const NotificationStateIcon =
    permission === 'granted'
      ? NotificationsActiveIcon
      : permission === 'denied'
        ? NotificationsOffIcon
        : NotificationsIcon

  return (
    <NotificationsIconButton
      color='inherit'
      aria-label='Включить уведомления о новых заказах'
      title='Уведомления о новых заказах'
      onClick={() => {
        void handleEnableNotifications()
      }}
    >
      <NotificationStateIcon />
    </NotificationsIconButton>
  )
}

export default EnableNotificationsButton
